import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Select, DatePicker } from "antd";
import dayjs from "dayjs";
import 'dayjs/locale/ko';
import ko_KR from 'antd/es/date-picker/locale/ko_KR';
import en_US from 'antd/es/date-picker/locale/en_US';
import styled, { createGlobalStyle } from 'styled-components';
import {
  AssetOverallHeaderSection,
  FilterRow,
  FilterItem,
  FilterLabel,
  HeaderTitle,
  HeaderDescription,
} from "../styles";

const { RangePicker } = DatePicker;

/* 전역 캘린더 스타일 - 피그마 디자인 반영 */
const GlobalCalendarStyles = createGlobalStyle`
  .asset-overall-calendar.ant-picker-dropdown {
    z-index: 9999 !important;
    padding: 0 !important;
    background: #0d1117 !important;
    border-radius: 12px !important;
    overflow: hidden !important;
  }

  /* PC에서 달력 하나만 표시 */
  .asset-overall-calendar .ant-picker-panels {
    flex-direction: row !important;
  }

  .asset-overall-calendar .ant-picker-panel-container {
    display: flex;
    flex-direction: row;
    background: #0d1117 !important;
  }

  .asset-overall-calendar .ant-picker-panel:last-child {
    display: none !important;
  }

  .asset-overall-calendar .ant-picker-panel:first-child {
    width: 100% !important;
  }

  .asset-overall-calendar .ant-picker-panel {
    background: #0d1117 !important;
    border: none !important;
    width: 335px !important;
    padding: 0 !important;
  }

  /* 헤더 스타일 - flexbox로 재배치 */
  .asset-overall-calendar .ant-picker-header {
    height: 54px !important;
    padding: 0 20px 0 30px !important;
    border-bottom: 1px solid #353535 !important;
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    background: transparent !important;
    margin: 0 !important;
  }

  /* 년도 이동 버튼 숨김 */
  .asset-overall-calendar .ant-picker-header-super-prev-btn,
  .asset-overall-calendar .ant-picker-header-super-next-btn {
    display: none !important;
  }

  /* 헤더 월/년 텍스트 - 왼쪽 배치, order: 1 */
  .asset-overall-calendar .ant-picker-header-view {
    font-family: 'Aggro', sans-serif !important;
    font-weight: 500 !important;
    font-size: 16px !important;
    color: #ffffff !important;
    letter-spacing: -0.32px !important;
    line-height: normal !important;
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    margin: 0 !important;
    padding: 0 !important;
    order: 1 !important;
    flex: 1 !important;
    justify-content: flex-start !important;
  }

  .asset-overall-calendar .ant-picker-header-view button {
    color: #ffffff !important;
    font-family: 'Aggro', sans-serif !important;
    font-weight: 500 !important;
    font-size: 16px !important;
    pointer-events: none !important;
  }

  /* 왼쪽 화살표 (이전) - 오른쪽으로 이동, order: 2 */
  .asset-overall-calendar .ant-picker-header-prev-btn {
    width: 30px !important;
    height: 30px !important;
    border-radius: 6px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    background: transparent !important;
    color: #ffffff !important;
    min-width: 30px !important;
    flex-shrink: 0 !important;
    order: 2 !important;
    margin-left: auto !important;
  }

  /* 오른쪽 화살표 (다음) - order: 3, 강제 표시 */
  .asset-overall-calendar .ant-picker-header-next-btn {
    width: 30px !important;
    height: 30px !important;
    border-radius: 6px !important;
    padding: 0 !important;
    display: flex !important;
    visibility: visible !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    background: transparent !important;
    color: #ffffff !important;
    min-width: 30px !important;
    flex-shrink: 0 !important;
    order: 3 !important;
    opacity: 1 !important;
  }

  /* RangePicker 첫번째 패널에서 next 버튼 강제 표시 */
  .asset-overall-calendar .ant-picker-panel:first-child .ant-picker-header-next-btn {
    display: flex !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  .asset-overall-calendar .ant-picker-header-prev-btn:hover,
  .asset-overall-calendar .ant-picker-header-next-btn:hover {
    background: rgba(255, 255, 255, 0.1) !important;
  }

  .asset-overall-calendar .ant-picker-header .ant-picker-prev-icon,
  .asset-overall-calendar .ant-picker-header .ant-picker-next-icon {
    color: #ffffff !important;
  }

  .asset-overall-calendar .ant-picker-header .ant-picker-prev-icon::before,
  .asset-overall-calendar .ant-picker-header .ant-picker-next-icon::before {
    border-color: #ffffff !important;
    border-width: 1.5px 0 0 1.5px !important;
    width: 8px !important;
    height: 8px !important;
  }

  /* 요일 헤더 스타일 */
  .asset-overall-calendar .ant-picker-content {
    width: 100% !important;
  }

  .asset-overall-calendar .ant-picker-content thead {
    border-bottom: none !important;
  }

  .asset-overall-calendar .ant-picker-content th {
    font-family: 'NanumSquareRound', sans-serif !important;
    font-size: 12px !important;
    font-weight: 400 !important;
    color: #777986 !important;
    padding: 8px 4px !important;
    height: auto !important;
    border: none !important;
    text-align: center !important;
    line-height: normal !important;
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: clip !important;
    vertical-align: middle !important;
    min-width: 36px !important;
  }

  /* 날짜 셀 스타일 */
  .asset-overall-calendar .ant-picker-content tbody {
    border-top: none !important;
  }

  .asset-overall-calendar .ant-picker-cell {
    height: 42px !important;
    padding: 0 !important;
    border: none !important;
    text-align: center !important;
    vertical-align: middle !important;
    position: relative !important;
  }

  .asset-overall-calendar .ant-picker-cell-inner {
    width: 100% !important;
    height: 42px !important;
    padding: 0 !important;
    font-family: 'NanumSquareRound', sans-serif !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    color: #b3b5bd !important;
    border-radius: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    line-height: 42px !important;
    text-align: center !important;
    margin: 0 auto !important;
    position: relative !important;
    background: transparent !important;
  }

  /* 이전/다음 달 날짜 스타일 */
  .asset-overall-calendar .ant-picker-cell:not(.ant-picker-cell-in-view) .ant-picker-cell-inner {
    color: #5b5d6b !important;
  }

  /* 선택된 날짜 범위 배경 - 중간 날짜 */
  .asset-overall-calendar .ant-picker-cell-in-range::before {
    content: '' !important;
    position: absolute !important;
    top: 50% !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    height: 42px !important;
    background: #07d993 !important;
    border-radius: 0 !important;
    z-index: 0 !important;
    transform: translateY(-50%) !important;
  }

  /* 시작일 배경 - 왼쪽만 둥글게 */
  .asset-overall-calendar .ant-picker-cell-range-start::before {
    content: '' !important;
    position: absolute !important;
    height: 42px !important;
    width: 100% !important;
    top: 50% !important;
    left: 0 !important;
    right: 0 !important;
    background: #07d993 !important;
    z-index: 0 !important;
    transform: translateY(-50%) !important;
    border-radius: 999px 0 0 999px !important;
  }

  /* 종료일 배경 - 오른쪽만 둥글게 */
  .asset-overall-calendar .ant-picker-cell-range-end::before {
    content: '' !important;
    position: absolute !important;
    height: 42px !important;
    width: 100% !important;
    top: 50% !important;
    left: 0 !important;
    right: 0 !important;
    background: #07d993 !important;
    z-index: 0 !important;
    transform: translateY(-50%) !important;
    border-radius: 0 999px 999px 0 !important;
  }

  /* 시작일과 종료일이 같은 날짜인 경우 */
  .asset-overall-calendar .ant-picker-cell-range-start.ant-picker-cell-range-end::before {
    border-radius: 999px !important;
  }

  /* 선택된 날짜 텍스트 색상 */
  .asset-overall-calendar .ant-picker-cell-range-start .ant-picker-cell-inner,
  .asset-overall-calendar .ant-picker-cell-range-end .ant-picker-cell-inner,
  .asset-overall-calendar .ant-picker-cell-in-range .ant-picker-cell-inner {
    color: #0d1117 !important;
    font-weight: 400 !important;
    position: relative !important;
    z-index: 2 !important;
    background: transparent !important;
  }

  /* 오늘 날짜 스타일 */
  .asset-overall-calendar .ant-picker-cell-today:not(.ant-picker-cell-in-range):not(.ant-picker-cell-range-start):not(.ant-picker-cell-range-end) .ant-picker-cell-inner {
    border: none !important;
    color: #b3b5bd !important;
  }

  .asset-overall-calendar .ant-picker-cell-today .ant-picker-cell-inner::before {
    display: none !important;
  }

  /* 호버 효과 */
  .asset-overall-calendar .ant-picker-cell:not(.ant-picker-cell-in-range):not(.ant-picker-cell-range-start):not(.ant-picker-cell-range-end):hover:not(.ant-picker-cell-disabled) .ant-picker-cell-inner {
    background: rgba(255, 255, 255, 0.1) !important;
    border-radius: 999px !important;
  }

  /* 선택 범위 호버 시 배경 유지 */
  .asset-overall-calendar .ant-picker-cell-in-range:hover .ant-picker-cell-inner,
  .asset-overall-calendar .ant-picker-cell-range-start:hover .ant-picker-cell-inner,
  .asset-overall-calendar .ant-picker-cell-range-end:hover .ant-picker-cell-inner {
    background: transparent !important;
  }

  /* 패널 내부 패딩 */
  .asset-overall-calendar .ant-picker-body {
    padding: 10px 20px 16px 20px !important;
  }

  /* 테이블 너비 조정 */
  .asset-overall-calendar .ant-picker-content table {
    width: 295px !important;
    margin: 0 auto !important;
  }

  /* 범위 호버 하이라이트 제거 */
  .asset-overall-calendar .ant-picker-cell-range-hover::before,
  .asset-overall-calendar .ant-picker-cell-range-hover-start::before,
  .asset-overall-calendar .ant-picker-cell-range-hover-end::before {
    display: none !important;
  }
`;

const StyledRangePicker = styled(RangePicker)`
  min-width: 240px;
`;

interface AssetOverallHeaderProps {
  tradingType: string;
  setTradingType: (value: string) => void;
  walletType: string;
  setWalletType: (value: string) => void;
  transactionType: string;
  setTransactionType: (value: string) => void;
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  setDateRange: (value: [dayjs.Dayjs | null, dayjs.Dayjs | null]) => void;
}

const AssetOverallHeader: React.FC<AssetOverallHeaderProps> = ({
  tradingType,
  setTradingType,
  walletType,
  setWalletType,
  transactionType,
  setTransactionType,
  dateRange,
  setDateRange,
}) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (i18n.language === 'ko') {
      dayjs.locale('ko');
    } else {
      dayjs.locale('en');
    }
  }, [i18n.language]);

  const tradingTypeOptions = [
    { value: "real", label: t("futuresTrading") },
    { value: "copy", label: t("copyTrading") },
    { value: "bot", label: t("botTrading") },
  ];

  const walletTypeOptions = [
    { value: "all", label: t("allWallets") },
    { value: "0", label: t("spotWallet") },
    { value: "1", label: t("futuresWallet") },
  ];

  const transactionTypeOptions = [
    { value: "all", label: t("allTransactions") },
    { value: "DEPOSIT", label: t("deposit") },
    { value: "WITHDRAWAL", label: t("withdrawal") },
    { value: "ADMIN_DEPOSIT", label: t("adminDeposit") },
    { value: "TRANSFER_IN", label: t("transferIn") },
    { value: "TRANSFER_OUT", label: t("transferOut") },
    { value: "TRADE_FEE", label: t("tradeFee") },
    { value: "REALIZED_PNL", label: t("realizedPnl") },
    { value: "COMMISSION", label: t("referralCommission") },
    { value: "REWARD", label: t("reward") },
    { value: "FUNDING_FEE_PAID", label: t("fundingFeeMinus") },
    { value: "FUNDING_FEE_RECEIVED", label: t("fundingFeePlus") },
    { value: "LIQUIDATION", label: t("forcedLiquidation") },
  ];

  const selectStyle = {
    width: "100%",
    minWidth: 140,
  };

  return (
    <>
      <GlobalCalendarStyles />
      <AssetOverallHeaderSection>
        <HeaderTitle>
          📊 {t("overallHistory")}
        </HeaderTitle>
      <HeaderDescription>
        {t("overallHistoryDescription")}
      </HeaderDescription>

      <FilterRow>
        <FilterItem>
          <FilterLabel>{t("tradingType")}</FilterLabel>
          <Select
            value={tradingType}
            onChange={setTradingType}
            options={tradingTypeOptions}
            style={selectStyle}
          />
        </FilterItem>

        <FilterItem>
          <FilterLabel>{t("walletType")}</FilterLabel>
          <Select
            value={walletType}
            onChange={setWalletType}
            options={walletTypeOptions}
            style={selectStyle}
          />
        </FilterItem>

        <FilterItem>
          <FilterLabel>{t("transactionType")}</FilterLabel>
          <Select
            value={transactionType}
            onChange={setTransactionType}
            options={transactionTypeOptions}
            style={selectStyle}
          />
        </FilterItem>

        <FilterItem>
          <FilterLabel>{t("dateRange")}</FilterLabel>
          <StyledRangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
            popupClassName="asset-overall-calendar"
            locale={i18n.language === 'ko' ? ko_KR : en_US}
            allowClear
          />
        </FilterItem>
      </FilterRow>
      </AssetOverallHeaderSection>
    </>
  );
};

export default AssetOverallHeader;
